// Ma'lumotnoma so'rovlari — bir necha feature'ga BIR VAQTDA kerak bo'ladigan
// kichik, faqat o'qishga mo'ljallangan ro'yxatlar.
//
// NEGA `shared/` DA, feature'da EMAS (07-FRONTEND.md §6): "features/* bir-birini
// import qilmaydi" degan qoida bor. Abonentlar jadvaliga esa ikkita begona
// narsa kerak:
//   · kim HOZIR onlayn  → `session` moduli
//   · paket nomi/narxi  → `catalog` moduli
// `features/subscriber` ularni to'g'ridan-to'g'ri import qilsa, uch modul
// bir-biriga yopishib qolardi va backend chegaralarining frontend ko'zgusi
// buzilardi.
//
// Sxemalar ATAYLAB tor — bu yerda faqat tanlov va qidiruv uchun kerak
// maydonlar bor. To'liq shakl o'z feature'ida qoladi, ya'ni bu nusxa emas,
// boshqa (kichikroq) kontrakt.

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { api } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queryKeys'
import { moneySchema, planSellable, toMoney } from '@/shared/api/schemas'
import { displayName } from '@/shared/lib/planName'
import type { Money } from '@/shared/lib/money'

const EMPTY_MAP: ReadonlyMap<string, string> = new Map<string, string>()
const EMPTY_PLANS: readonly PlanOption[] = []
const EMPTY_POLICIES: readonly PolicyOption[] = []
const EMPTY_VISPS: readonly VispOption[] = []

const realmSchema = z.object({ realm: z.string().nullish() })

/**
 * Joriy tashkilotning RADIUS realm'i — `GET /api/subscribers/radius-realm`.
 *
 * NEGA KERAK: X-SIB'da login toza (`room101`), RADIUS'ga esa
 * `room101@uz-venue` yoziladi. Operator abonentga AYNAN ikkinchisini
 * berishi kerak; birinchisini bersa abonent `Access-Reject` oladi va
 * panelda hech qanday belgi bo'lmaydi.
 *
 * `staleTime: Infinity` — realm tashkilot sozlamasi, sessiya davomida
 * o'zgarmaydi. Har kartochka ochilishida qayta so'rash keraksiz yuk.
 *
 * ⚠️ Xato bo'lsa `null` qaytadi va chaqiruvchi realmni KO'RSATMASLIGI
 * kerak. Taxminiy qiymat (masalan tenant kodi) chizish eng yomon
 * variant bo'lardi: operator noto'g'ri loginni ishonch bilan berardi.
 */
export function useRadiusRealm(enabled = true) {
  const query = useQuery({
    queryKey: queryKeys.subscribers.radiusRealm,
    queryFn: async (): Promise<string | null> => {
      const response = await api.get<unknown>('/api/subscribers/radius-realm')
      return realmSchema.parse(response.data).realm ?? null
    },
    enabled,
    staleTime: Number.POSITIVE_INFINITY,
  })

  return query.data ?? null
}

const planOptionSchema = z.array(
  z.object({
    id: z.string(),
    name: z.string(),
    /**
     * Nom tarjimalari `{"ru": "...", "en": "..."}`.
     *
     * ⚠️ `nullish()` — bugun jonli bazada BARCHA qatorda `NULL`
     * (`QARORLAR.md` §30.39, o'lchangan): ustun bor, ma'lumot yo'q.
     * `optional` bo'lmasa butun katalog sahifasi o'chib qolardi.
     */
    nameI18n: z.record(z.string(), z.string()).nullish(),
    price: moneySchema,
    /** ⚠️ NULL bo'lishi mumkin — "faqat kvota beradigan paket" */
    policyId: z.string().nullish(),
    /**
     * Backend hisoblab beradi: `state == ACTIVE && !misconfigured`.
     *
     * ⚠️ Eski build'da bu maydon YO'Q — shuning uchun `nullish()`, va
     * zaxira qoida `planSellable` da.
     */
    sellable: z.boolean().nullish(),
    state: z.string(),
    deliveryMethod: z.string(),
  }),
)

export interface PlanOption {
  id: string
  /** ⚠️ KANONIK nom — kalit, eksport va chop etish shu nomni oladi */
  name: string
  /** Nom tarjimalari; bo'sh xarita = tarjima yo'q (`null` EMAS) */
  nameI18n: Record<string, string>
  price: Money
  /** `null` = siyosatga bog'lanmagan: tezlikka tegmaydi, faqat kvota beradi */
  policyId: string | null
  state: string
  deliveryMethod: string
  /** Hozir sotish mumkinmi */
  sellable: boolean
}

/**
 * Paketlar — tanlov ro'yxatlari uchun.
 *
 * BITTA so'rov: ikkala nom xaritasi ham shu keshdan o'qiydi, ya'ni bitta sahifada
 * ikkalasi ishlatilsa ham katalog ikki marta yuklanmaydi.
 */
export function usePlanOptions(enabled = true): readonly PlanOption[] {
  const query = useQuery({
    queryKey: queryKeys.catalog.planIndex,
    queryFn: async (): Promise<PlanOption[]> => {
      const response = await api.get<unknown>('/api/catalog/plans')
      return planOptionSchema.parse(response.data).map((plan) => ({
        id: plan.id,
        name: plan.name,
        nameI18n: plan.nameI18n ?? {},
        price: toMoney(plan.price),
        policyId: plan.policyId ?? null,
        state: plan.state,
        deliveryMethod: plan.deliveryMethod,
        // ⚠️ Ilgari bu yerda `plan.state === 'ACTIVE'` turardi va aynan shu
        // qator siyosati UNUTILGAN `tarif-15m` ni «Paket sotish» ro'yxatida
        // qoldirgan edi: u ACTIVE, ya'ni panel uchun sotiladigan ko'rinardi.
        sellable: planSellable(plan),
      }))
    },
    enabled,
    // Katalog kamdan-kam o'zgaradi — 5 daqiqa yetarli
    staleTime: 5 * 60_000,
  })

  return query.data ?? EMPTY_PLANS
}

const EMPTY_DENIED: ReadonlySet<string> = new Set()

/**
 * Shu filial abonentiga joriy foydalanuvchi sota olmaydigan paketlar (ADR-047) — sotuv ro'yxatlaridan yashiriladi.
 * Faqat qulaylik: server sotuvni baribir tekshiradi. `null` — filialsiz abonent, `undefined` — abonent hali noma'lum.
 */
export function useDeniedPlans(branchId: string | null | undefined, enabled = true): ReadonlySet<string> {
  const query = useQuery({
    queryKey: queryKeys.catalog.deniedPlans(branchId ?? null),
    queryFn: async ({ signal }) => {
      const response = await api.get<unknown>('/api/catalog/plans/denied', {
        signal,
        params: branchId ? { branchId } : {},
      })
      return new Set(z.array(z.string()).parse(response.data))
    },
    enabled: enabled && branchId !== undefined,
    staleTime: 60_000,
  })
  return query.data ?? EMPTY_DENIED
}

/**
 * Plan id → <b>KANONIK</b> nom xaritasi.
 *
 * Abonentlar ro'yxati faqat `activePlanId` beradi (nom emas), ya'ni nomni
 * ko'rsatish uchun katalog kerak. Har qator uchun alohida so'rov 50 qatorli
 * sahifada 50 ta chaqiruv bo'lardi — shuning uchun katalog BIR MARTA
 * yuklanadi va xaritaga aylantiriladi.
 *
 * <h2>⚠️ NEGA NOMIDA «CANONICAL» BOR</h2>
 * Bu ilgari `usePlanIndex` deb atalardi va uni EKRAN ustuni ham, CSV
 * EKSPORTI ham, chop etiladigan QR KARTA ham bitta chaqiruvdan olardi
 * (`SubscriberTable.tsx` da bir xil `planIndex` 95 va 199-qatorlarda).
 * Ya'ni "ekran tarjima qilsin" degan bitta o'zgarish eksportni va
 * qog'ozni ham JIMGINA tarjima qilib yuborardi — mashina qayta
 * o'qiydigan faylga tarjima tushishi esa aynan yo'l qo'yilmaydigan narsa
 * (`QARORLAR.md` §30.6, §30.39).
 *
 * Shuning uchun ikkita xarita bor va ular <b>nomidan</b> farq qiladi:
 * bu yerda — kanonik, {@link usePlanDisplayIndex} da — tarjima qilingan.
 * Chaqiruv joyida qaysi biri tanlanganini o'qib ko'rish mumkin, izohga
 * qarash shart emas.
 *
 * Kanonik nomni oladiganlar: CSV/XLSX eksport, chop etiladigan QR karta.
 */
export function useCanonicalPlanIndex(enabled = true): ReadonlyMap<string, string> {
  const plans = usePlanOptions(enabled)
  return useMemo(() => canonicalPlanIndex(plans), [plans])
}

/**
 * {@link useCanonicalPlanIndex} ning HOOK'SIZ yadrosi.
 *
 * ⚠️ Nega ajratilgan: hook'ni React'siz chaqirib bo'lmaydi, ya'ni
 * "kanonik bo'lib qoldimi?" degan savolga faqat MANBA NAQSHI bilan
 * javob berish mumkin bo'lardi. Naqsh esa qayta yozilganda jimgina
 * mos kelmay qoladi. Yadro alohida bo'lgani uchun
 * `tests/plan-name-surface.mjs` uni HAQIQATDAN chaqiradi — va
 * tarjimasi ANIQ BOR fikstura bilan (jonli bazada bugun `name_i18n`
 * hamma qatorda `NULL`, §30.39).
 */
export function canonicalPlanIndex(
  plans: readonly PlanOption[],
): ReadonlyMap<string, string> {
  return plans.length === 0 ? EMPTY_MAP : new Map(plans.map((p) => [p.id, p.name]))
}

/**
 * Plan id → EKRANDA ko'rinadigan nom (tarjima bo'lsa tarjima).
 *
 * Zaxira zanjiri `shared/lib/planName.ts` da, u esa backenddagi
 * `LocalizedName` ning ko'zgusi: tarjima yo'q bo'lsa kanonik nom
 * qaytadi, ya'ni bu xarita hech qachon bo'sh katak bermaydi.
 *
 * ⚠️ Faqat EKRAN uchun. Eksport va chop etish uchun
 * {@link useCanonicalPlanIndex}.
 */
export function usePlanDisplayIndex(enabled = true): ReadonlyMap<string, string> {
  const plans = usePlanOptions(enabled)
  const { i18n } = useTranslation()
  const language = i18n.language
  return useMemo(() => planDisplayIndex(plans, language), [plans, language])
}

/** {@link usePlanDisplayIndex} ning HOOK'SIZ yadrosi — sabab yuqorida. */
export function planDisplayIndex(
  plans: readonly PlanOption[],
  language: string | null | undefined,
): ReadonlyMap<string, string> {
  return plans.length === 0
    ? EMPTY_MAP
    : new Map(plans.map((p) => [p.id, displayName(p, language)]))
}

const policyOptionSchema = z.array(
  z.object({
    id: z.string(),
    name: z.string(),
    /** ⚠️ `planOptionSchema.nameI18n` bilan bir xil sabab — bugun hamma qatorda NULL */
    nameI18n: z.record(z.string(), z.string()).nullish(),
    rateLimit: z.string().nullish(),
  }),
)

export interface PolicyOption {
  id: string
  /** ⚠️ KANONIK nom */
  name: string
  /** Nom tarjimalari; bo'sh xarita = tarjima yo'q */
  nameI18n: Record<string, string>
  /** MikroTik shakli, masalan "50M/50M" — tanlov ro'yxatida ko'rsatish uchun */
  rateLimit: string | null
}

/**
 * Siyosatlar (Policy) — `venue` zona formasida `policyId` tanlash uchun.
 *
 * `venue` moduli `catalog`ga BOG'LANMAYDI (backend `VenueZone.java`
 * javadoc: "bu modulda faqat uzatiladi, ishlatilmaydi"), shuning uchun bu
 * yerda ham — `shared/`da, `features/catalog` emas: aks holda
 * `features/venue` `features/catalog`ni import qilardi va bu backend modul
 * chegarasini buzardi (07-FRONTEND.md §6).
 */
export function usePolicyOptions(enabled = true): readonly PolicyOption[] {
  const query = useQuery({
    queryKey: queryKeys.catalog.policyIndex,
    queryFn: async (): Promise<PolicyOption[]> => {
      const response = await api.get<unknown>('/api/catalog/policies')
      return policyOptionSchema.parse(response.data).map((policy) => ({
        id: policy.id,
        name: policy.name,
        nameI18n: policy.nameI18n ?? {},
        rateLimit: policy.rateLimit ?? null,
      }))
    },
    enabled,
    staleTime: 5 * 60_000,
  })

  return query.data ?? EMPTY_POLICIES
}

const vispOptionSchema = z.array(
  z.object({
    id: z.string(),
    name: z.string(),
    centerId: z.string(),
    centerName: z.string(),
    status: z.string(),
    centerStatus: z.string(),
    isDefault: z.boolean(),
    quota: z.number().nullish(),
    accounts: z.object({ available: z.number() }).nullish(),
  }),
)

export interface VispOption {
  id: string
  name: string
  centerId: string
  centerName: string
  isDefault: boolean
  /** VISP va uning Centeri faol — abonentni faqat shunga o'tkazish mumkin */
  assignable: boolean
  /** Kvotali VISP: abonent faqat uning hovuzidan ro'yxatdan o'tadi, ko'chirib kiritilmaydi */
  pooled: boolean
  available: number
}

/** VISPlar — abonent filtri, kartochkasi va yaratish formasi uchun (`network.visp.read`). */
export function useVispOptions(enabled = true): readonly VispOption[] {
  const query = useQuery({
    queryKey: queryKeys.network.vispOptions,
    queryFn: async (): Promise<VispOption[]> => {
      const response = await api.get<unknown>('/api/network/visps')
      return vispOptionSchema.parse(response.data).map((visp) => ({
        id: visp.id,
        name: visp.name,
        centerId: visp.centerId,
        centerName: visp.centerName,
        isDefault: visp.isDefault,
        assignable: visp.status === 'ACTIVE' && visp.centerStatus === 'ACTIVE',
        pooled: visp.quota != null,
        available: visp.accounts?.available ?? 0,
      }))
    },
    enabled,
    staleTime: 60_000,
  })

  return query.data ?? EMPTY_VISPS
}

const subscriberCountSchema = z.object({ total: z.number() })

export interface SubscriberCountUnder {
  /** `null` — hali yuklanmagan yoki so'rov yiqilgan */
  count: number | null
  isLoading: boolean
  isError: boolean
}

/**
 * Tashkiliy tugun OSTIDAGI abonentlar soni — daraxtda ko'chirishning
 * oqibatini OLDINDAN ko'rsatish uchun (`QARORLAR.md` §19.8).
 *
 * Ko'chirish `subscriber.reseller_path` ni kaskad bilan yangilaydi, ya'ni
 * `SUBTREE` doiradagi operatorlar KO'RADIGAN abonentlar to'plami
 * o'zgaradi. Operator buni tugmani bosishdan OLDIN bilishi kerak.
 *
 * <h2>⚠️ NEGA `shared/` DA, `features/org` DA EMAS</h2>
 * `features/*` bir-birini import qilmaydi (07-FRONTEND.md §6), so'rov esa
 * `xsib-subscriber` ning endpointiga boradi. Sxema ATAYLAB tor — bu
 * yerda faqat `total` kerak.
 *
 * <h2>⚠️ BU HISOB HAR TUGUN UCHUN ISHLAMAYDI — VA BUNI CHAQIRUVCHI BILISHI SHART</h2>
 * `GET /api/subscribers?resellerId=X` filtri yo'lni `agent.party_node`
 * dan oladi (`ResellerLabel.PATH_SOURCE_SQL`), u esa
 * `platform.org_node` ustidagi moslik VIEW i va
 * <b>`is_settlement_party` bo'yicha FILTRLANGAN</b> (migratsiya 5.1).
 *
 * Ya'ni ombor yoki filial id'si berilsa, ichki `SELECT` hech nima
 * topmaydi va predikat rost bo'lmaydi — javob <b>0</b> bo'ladi.
 * Nol esa "abonent yo'q" degan ma'noni bildirmaydi; u "savol umuman
 * berilmadi" degani. Aynan shu narsa jimgina yolg'on bo'lardi:
 * operator "hech kim ta'sirlanmaydi" deb o'qib, butun filialni
 * ko'chirib yuborardi.
 *
 * Shuning uchun chaqiruvchi `enabled` ni turning IMKONIYATIGA qarab
 * beradi (`type.settlementParty`), NOMIGA qarab emas (§19.3) — va
 * imkoniyat yo'q bo'lsa raqam o'rniga sababini ko'rsatadi.
 *
 * ⚠️ `includeClosed: true` — yopilgan abonentning `reseller_path` i ham
 * kaskad bilan yangilanadi, ya'ni u ham "ta'sirlanadi". Ularni chiqarib
 * tashlash raqamni haqiqiydan kichik ko'rsatardi.
 */
export function useSubscriberCountUnder(
  nodeId: string | null,
  enabled: boolean,
): SubscriberCountUnder {
  const query = useQuery({
    queryKey: queryKeys.subscribers.countUnder(nodeId ?? ''),
    queryFn: async (): Promise<number> => {
      const response = await api.get<unknown>('/api/subscribers', {
        // `size: 1` — bizga faqat `total` kerak, qatorlar emas.
        params: { resellerId: nodeId, page: 0, size: 1, includeClosed: true },
      })
      return subscriberCountSchema.parse(response.data).total
    },
    enabled: enabled && nodeId !== null,
    staleTime: 30_000,
  })

  return {
    count: query.data ?? null,
    isLoading: query.isPending && query.fetchStatus !== 'idle',
    isError: query.isError,
  }
}
